// A custom window's games: the KPI dashboard's (Trevor 2026-10-02) and the team report picker's (any team, 2026-10-27).
// A checkbox per game, oldest first down each column so the season reads like a calendar (Trevor 2026-10-27), each
// numbered by its place in the team's regular season; shift-click selects the run from the last clicked game; Home /
// Away, Wins / Losses and a player's Played / Didn't play (the report picker's and the KPI page's lists say who played)
// narrow the list, and "Select all" / "Clear" act on what the filters show.
import { CSSProperties, useMemo, useRef, useState } from "react";
import "./gamePicker.scss";

/** One pickable game (the KPI games list's and the custom report games list's rows). */
export interface PickerGame {
  gameId: string;
  /** YYYY-MM-DD */
  date: string;
  opponent: string;
  home: boolean;
  pts?: number;
  oppPts?: number;
  result?: "W" | "L";
  /** The team's players who played (core ids); absent when the game has no lineup data, so neither player filter
   * shows it. */
  played?: string[];
}

/** One of the team's players for the played / didn't play filter. */
export interface PickerPlayer {
  id: string;
  name: string;
  games: number;
}

type Side = "all" | "home" | "away";
type Res = "all" | "W" | "L";
type Presence = "played" | "missed";

export default function GamePicker({
  games,
  picked,
  onChange,
  players = [],
}: {
  games: PickerGame[];
  picked: string[];
  onChange: (ids: string[]) => void;
  players?: PickerPlayer[];
}) {
  const [side, setSide] = useState<Side>("all");
  const [res, setRes] = useState<Res>("all");
  const [presence, setPresence] = useState<Presence>("played");
  const [player, setPlayer] = useState("");
  // a player picked for another team's list no longer filters
  const who = players.some((p) => p.id === player) ? player : "";
  const narrowed = side !== "all" || res !== "all" || !!who;
  const anchor = useRef<number | null>(null);
  // Numbered before the filters narrow the list, so a game keeps its number with Away or Losses on.
  const season = useMemo(
    () =>
      [...games].sort(
        (a, b) =>
          a.date.localeCompare(b.date) || a.gameId.localeCompare(b.gameId),
      ),
    [games],
  );
  const number = useMemo(
    () => new Map(season.map((g, i) => [g.gameId, i + 1])),
    [season],
  );
  const shown = useMemo(
    () =>
      season.filter(
        (g) =>
          (side === "all" || (side === "home") === g.home) &&
          (res === "all" || g.result === res) &&
          (!who ||
            (g.played !== undefined &&
              g.played.includes(who) === (presence === "played"))),
      ),
    [season, side, res, who, presence],
  );
  // Filled down each column: the grid needs its row count, one per column count the stylesheet uses. A short list
  // runs five deep before it starts the next column (Trevor 2026-10-27), so a few games stay one readable run.
  const depth = (cols: number) =>
    Math.max(Math.min(shown.length, 5), Math.ceil(shown.length / cols));
  const rows = {
    "--gp-rows": depth(4),
    "--gp-rows-narrow": depth(2),
  } as CSSProperties;
  const sel = useMemo(() => new Set(picked), [picked]);

  const click = (i: number, shift: boolean) => {
    const next = new Set(sel);
    const on = !sel.has(shown[i].gameId);
    if (shift && anchor.current != null) {
      const [a, b] = [Math.min(anchor.current, i), Math.max(anchor.current, i)];
      for (let j = a; j <= b; j++)
        on ? next.add(shown[j].gameId) : next.delete(shown[j].gameId);
    } else {
      on ? next.add(shown[i].gameId) : next.delete(shown[i].gameId);
    }
    anchor.current = i;
    onChange(games.filter((g) => next.has(g.gameId)).map((g) => g.gameId));
  };
  const all = () =>
    onChange(
      games
        .filter((g) => sel.has(g.gameId) || shown.includes(g))
        .map((g) => g.gameId),
    );
  const clear = () =>
    onChange(picked.filter((id) => !shown.some((g) => g.gameId === id)));
  const seg = <T extends string>(
    val: T,
    set: (v: T) => void,
    opts: [T, string][],
    off = false,
  ) => (
    <span className={`gp-seg${off ? " off" : ""}`}>
      {opts.map(([k, l]) => (
        <button
          key={k}
          className={val === k && !off ? "on" : ""}
          disabled={off}
          onClick={() => set(k)}
        >
          {l}
        </button>
      ))}
    </span>
  );
  return (
    <div className="game-picker">
      <div className="game-picker-bar">
        <b>
          Custom · {picked.length} {picked.length === 1 ? "game" : "games"}
        </b>
        {seg(side, setSide, [
          ["all", "All"],
          ["home", "Home"],
          ["away", "Away"],
        ])}
        {seg(res, setRes, [
          ["all", "All"],
          ["W", "Wins"],
          ["L", "Losses"],
        ])}
        {players.length > 0 && (
          <span className="gp-player">
            {seg(
              presence,
              setPresence,
              [
                ["played", "Played"],
                ["missed", "Didn't play"],
              ],
              !who,
            )}
            <select
              id="gp-player"
              aria-label="Player"
              value={who}
              onChange={(e) => setPlayer(e.target.value)}
            >
              <option value="">Any player</option>
              {players.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.games} GP
                </option>
              ))}
            </select>
          </span>
        )}
        <button className="gp-link" onClick={all}>
          Select all{narrowed ? " shown" : ""}
        </button>
        <button className="gp-link" onClick={clear}>
          Clear{narrowed ? " shown" : ""}
        </button>
        <span className="hint">Shift-click selects a run</span>
      </div>
      {games.length === 0 && (
        <div className="game-picker-empty">No finished games yet.</div>
      )}
      <div className="game-picker-list" style={rows}>
        {shown.map((g, i) => (
          <label
            key={g.gameId}
            className={sel.has(g.gameId) ? "on" : ""}
            onClick={(e) => {
              e.preventDefault();
              click(i, e.shiftKey);
            }}
          >
            <input type="checkbox" checked={sel.has(g.gameId)} readOnly />
            <span className="d">
              {g.date.slice(5).replace("-", "/").replace(/^0/, "")}
            </span>
            <span className="n">G{number.get(g.gameId)}</span>
            <span className="o">
              {g.home ? "vs" : "@"} {g.opponent}
            </span>
            {g.result && (
              <span className={`r ${g.result === "W" ? "w" : "l"}`}>
                {g.result} {g.pts}–{g.oppPts}
              </span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}
